import { useQuery } from '@tanstack/react-query';
import { 
  FileText, 
  Sparkles, 
  Receipt, 
  ArrowRight,
  Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, DashboardData } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/dashboard'),
  });

  const { data: trend } = useQuery({
    queryKey: ['monthly-trend'],
    queryFn: () => api.get<{ data: any[] }>('/api/dashboard/monthly-trend', { months: '6' }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const alerts = dashboard?.alerts || { pendingJournals: 0, pendingDrafts: 0, pendingEvidence: 0 };
  const monthlyPL = dashboard?.monthlyPL || { revenue: 0, expense: 0, profit: 0 };
  const recentJournals = dashboard?.recentJournals || [];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            ダッシュボード
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            おかえりなさい、{user?.name}さん
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <Calendar size={16} />
          {new Date().toLocaleDateString('ja-JP', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            weekday: 'long'
          })}
        </div>
      </div>

      {/* Alert cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/journals"
          className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <FileText className="text-orange-600" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              未処理仕訳
            </p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {alerts.pendingJournals}
              <span className="text-sm font-normal ml-1">件</span>
            </p>
          </div>
          <ArrowRight size={20} style={{ color: 'var(--text-muted)' }} />
        </Link>

        <Link
          to="/journals/auto"
          className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Sparkles className="text-purple-600" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              AI仕訳提案
            </p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {alerts.pendingDrafts}
              <span className="text-sm font-normal ml-1">件</span>
            </p>
          </div>
          <ArrowRight size={20} style={{ color: 'var(--text-muted)' }} />
        </Link>

        <Link
          to="/evidence"
          className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Receipt className="text-blue-600" size={24} />
          </div>
          <div className="flex-1">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              未処理証憑
            </p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {alerts.pendingEvidence}
              <span className="text-sm font-normal ml-1">件</span>
            </p>
          </div>
          <ArrowRight size={20} style={{ color: 'var(--text-muted)' }} />
        </Link>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly P/L & Chart */}
        <div className="lg:col-span-2 space-y-6">
          {/* Monthly P/L summary */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              今月の損益概算
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>売上</p>
                <p className="text-xl font-bold font-mono" style={{ color: 'var(--success)' }}>
                  ¥{monthlyPL.revenue.toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>経費</p>
                <p className="text-xl font-bold font-mono" style={{ color: 'var(--error)' }}>
                  ¥{monthlyPL.expense.toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>利益</p>
                <p className={`text-xl font-bold font-mono ${monthlyPL.profit >= 0 ? '' : 'text-red-600'}`}
                  style={{ color: monthlyPL.profit >= 0 ? 'var(--accent-color)' : undefined }}>
                  ¥{monthlyPL.profit.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              月次推移
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend?.data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)' }}
                  />
                  <YAxis 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)' }}
                    tickFormatter={(value) => `¥${(value / 10000).toFixed(0)}万`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--background-card)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`¥${value.toLocaleString()}`, '']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="売上"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="expense"
                    name="経費"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    name="利益"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent journals */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              最近の仕訳
            </h2>
            <Link
              to="/journals"
              className="text-sm hover:underline"
              style={{ color: 'var(--accent-color)' }}
            >
              すべて表示
            </Link>
          </div>
          <div className="space-y-3">
            {recentJournals.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                仕訳データがありません
              </p>
            ) : (
              recentJournals.map((journal) => (
                <div
                  key={journal.id}
                  className="p-3 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--light-gray-tertiary)',
                    borderColor: 'var(--border-color)',
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                      {journal.journalNumber}
                    </span>
                    <span
                      className={`badge ${
                        journal.status === 'posted'
                          ? 'badge-success'
                          : journal.status === 'draft'
                          ? 'badge-warning'
                          : 'badge-error'
                      }`}
                    >
                      {journal.status === 'posted' ? '転記済' : journal.status === 'draft' ? '下書き' : '取消'}
                    </span>
                  </div>
                  <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                    {journal.description || '（摘要なし）'}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {journal.journalDate}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

